import React, { Component, useEffect, useState } from 'react';
import {
  Table,
  Divider,
  Button,
  Empty,
  Icon,
  PageHeader,
  BackTop,
  Input,
  Badge,
  Spin,
  Row,
  Col,
  Tooltip,
  Popover,
  message
} from 'antd';
import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import PropTypes from 'prop-types';

import { LicenseCreateModal, LicenseDetailModal } from './';
import { fileExport } from '../../utils';

import styles from './LicenseTable.module.scss';

const { Column } = Table;

const FILTER_FIELDS = [
  { label: 'ID', value: 'id' },
  { label: 'Requester', value: 'requester_email' },
  { label: 'Audience', value: 'audience' },
  { label: 'Type', value: 'type' },
  { label: 'Use', value: 'use' },
  { label: 'Status', value: 'status' },
  { label: 'Created At', value: 'created_at' },
  { label: 'Expired At', value: 'exp_epoch' }
];
const FILTER_OPERATORS = [
  { label: 'equals', value: 'eq' },
  { label: 'not equals', value: 'neq' },
  { label: 'contains', value: 'like' },
  { label: 'not contains', value: 'notlike' },
  { label: 'greater than', value: 'gt' },
  { label: 'greater than or equals', value: 'gte' },
  { label: 'less than', value: 'lt' },
  { label: 'less than or equals', value: 'lte' }
];

function FilterBar({ filters, setFilters, onApply }) {
  const handleFieldChange = (idx, value) => {
    const newFilters = [...filters];
    newFilters[idx].field = value;
    setFilters(newFilters);
  };
  const handleOperatorChange = (idx, value) => {
    const newFilters = [...filters];
    newFilters[idx].operator = value;
    setFilters(newFilters);
  };
  const handleValueChange = (idx, value) => {
    const newFilters = [...filters];
    newFilters[idx].value = value;
    setFilters(newFilters);
  };
  const handleRemove = idx => {
    const newFilters = filters.filter((_, i) => i !== idx);
    setFilters(newFilters);
    // If all filters are removed, automatically apply empty filters
    if (newFilters.length === 0) {
      onApply([]);
    }
  };
  const handleAdd = () => {
    setFilters([...filters, { field: '', operator: 'eq', value: '' }]);
  };
  const handleApply = () => {
    // Convert date string to timestamp if field is exp_epoch
    const processedFilters = filters.map(f => {
      if (f.field === 'exp_epoch' && f.value) {
        // Check if value is already an integer (number or string representation of number)
        if (!isNaN(f.value) && !isNaN(parseInt(f.value)) && Number.isInteger(Number(f.value))) {
          return {
            ...f,
            value: parseInt(f.value)
          };
        } else {
          // If not a number, try to convert to date object and calculate timestamp
          try {
            return {
              ...f,
              value: Math.floor(new Date(f.value).getTime() / 1000)
            };
          } catch (error) {
            console.log('Invalid date format for exp_epoch:', f.value);
            // Return null to filter out invalid date filters
            return null;
          }
        }
      }
      return f;
    });
    onApply(processedFilters);
  };
  return (
    <div
      style={{
        marginBottom: 16,
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        background: '#fafafa',
        padding: 16
      }}
    >
      {filters.map((filter, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 8,
            alignItems: 'center',
            background: '#fff',
            border: '1px solid #e4e4e4',
            borderRadius: 4,
            padding: '8px 12px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}
        >
          <select
            value={filter.field}
            onChange={e => handleFieldChange(idx, e.target.value)}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '4px 8px',
              background: '#fafafa',
              minWidth: 120
            }}
          >
            <option value="">Field</option>
            {FILTER_FIELDS.map(f => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <select
            value={filter.operator}
            onChange={e => handleOperatorChange(idx, e.target.value)}
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '4px 8px',
              background: '#fafafa',
              minWidth: 120
            }}
          >
            {FILTER_OPERATORS.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          <input
            value={filter.value}
            onChange={e => handleValueChange(idx, e.target.value)}
            placeholder="Enter value..."
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: '4px 8px',
              background: '#fafafa',
              minWidth: 140
            }}
          />
          <Button onClick={() => handleRemove(idx)} size="small">
            X
          </Button>
        </div>
      ))}
      <Button onClick={handleAdd} size="small" type="default" style={{ marginRight: 8 }}>
        + Add Filter
      </Button>
      {filters.length > 0 && (
        <Button type="primary" onClick={handleApply} style={{ marginRight: 8 }}>
          Apply filters
        </Button>
      )}
    </div>
  );
}

class LicenseTable extends Component {
  // Map for all API field names
  fieldNameMap = {
    ID: 'id',
    RequesterEmail: 'requester_email',
    Audience: 'audience',
    Type: 'type',
    Use: 'use',
    Status: 'status',
    CreatedAt: 'created_at',
    ExpEpoch: 'exp_epoch'
  };

  handleLicenseDownload = (license, type, audience, expiredAt) => {
    const formattedExpiryDate = moment(expiredAt * 1000).format('MM-DD-YYYY');
    const formattedAudience = audience
      .toLowerCase()
      .trim()
      .replace(/\s+/gi, '-');

    fileExport(
      license,
      'text/plain;charset=utf-8',
      audience ? `tg-license-${type}-${formattedAudience}-${formattedExpiryDate}` : 'tg-license'
    );
  };

  renderExpiryDate = text => {
    // Text here is `ExpEpoch` property value of request obj
    // Its unit is second, so need to be multiplied by 1000 to get the millisecond value
    const expiredAt = text * 1000;
    const formattedString = moment(expiredAt).format('MMM DD, YYYY');

    const validPeriod = new Date(expiredAt).getTime() - new Date().getTime();

    const oneDayInMicroSecond = 60 * 60 * 24 * 1000;
    const quotient = validPeriod / oneDayInMicroSecond;

    const daysLeft = Math.floor(quotient);
    const dayUnit = daysLeft === 1 ? 'day' : 'days';
    const daysLeftString = `(${daysLeft} ${dayUnit} left)`;

    return quotient > 7 ? (
      <Tooltip placement="top" title={'Valid ' + daysLeftString}>
        <Badge color="green" text={formattedString} />
      </Tooltip>
    ) : quotient > 0 ? (
      <Tooltip placement="top" title={'Expires within 7 days ' + daysLeftString}>
        <Badge color="orange" text={formattedString} />
      </Tooltip>
    ) : (
      <Tooltip placement="top" title="Expired">
        <Badge color="red" text={formattedString} />
      </Tooltip>
    );
  };

  renderRequestStatus = text => (
    <span>
      {text.toLowerCase() === 'approved' ? (
        <Badge color="green" text={text} />
      ) : text.toLowerCase() === 'rejected' ? (
        <Badge color="red" text={text} />
      ) : (
        <Badge color="orange" text={text} />
      )}
    </span>
  );

  renderActionButtons = (text, record) => {
    const {
      ID: id,
      Audience: audience,
      Status: status,
      Type: type,
      ExpEpoch: expEpoch,
      CreatorEmail: email,
      ParamJSON: params,
      License: license,
      Notes: notes,
      NotifyInfo: notifyInfo
    } = record;

    return (
      <div className={styles.actionButtonsContainer}>
        <LicenseDetailModal
          id={id}
          status={status}
          notes={notes}
          detail={{ ...params, NotifyInfo: notifyInfo }}
          type={type}
          endTime={expEpoch * 1000}
          creator={email.split('@')[0]}
          isAdmin={this.props.isAdmin}
        />

        <LicenseCreateModal {...params} Type={type} />

        {license ? (
          <Tooltip title="Download license file">
            <Button
              className={styles.actionButton}
              onClick={() => this.handleLicenseDownload(license, type, audience, expEpoch)}
            >
              Download
            </Button>
          </Tooltip>
        ) : (
          <Tooltip title="Wait for approval">
            <Button className={styles.actionButton} disabled={true}>
              Download
            </Button>
          </Tooltip>
        )}
      </div>
    );
  };

  handleTableChange = (pagination, _filters, sorter) => {
    const { fetchLicense, sort, filter } = this.props;
    let sortStr;
    if (sorter && sorter.field) {
      const field = this.fieldNameMap[sorter.field] || sorter.field;
      sortStr = `${field}:${sorter.order === 'descend' ? 'desc' : 'asc'}`;
    } else {
      sortStr = sort;
    }
    fetchLicense(pagination.current, pagination.pageSize, sortStr, filter);
  };

  render() {
    let { data, total, page, pageSize, sort } = this.props;

    // Parse current sort field and order from sort string
    let currentSortField, currentSortOrder;
    if (sort) {
      const [field, order] = sort.split(':');
      currentSortField =
        Object.keys(this.fieldNameMap).find(k => this.fieldNameMap[k] === field) || field;
      currentSortOrder = order === 'desc' ? 'descend' : 'ascend';
    }

    const extractedData = data.map(item => {
      return {
        ...item,
        paramString: JSON.stringify(item.ParamJSON)
      };
    });

    const tablePagination = {
      current: page,
      pageSize: pageSize,
      total: total,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    };

    return extractedData.length !== 0 ? (
      <Table
        dataSource={extractedData}
        pagination={tablePagination}
        rowKey={record => record.ID}
        onChange={this.handleTableChange}
      >
        <Column
          title="ID"
          dataIndex="ID"
          sorter={true}
          sortOrder={currentSortField === 'ID' ? currentSortOrder : undefined}
        />
        <Column
          title="Requester"
          dataIndex="RequesterEmail"
          sorter={true}
          sortOrder={currentSortField === 'RequesterEmail' ? currentSortOrder : undefined}
        />
        <Column
          title="Audience"
          dataIndex="Audience"
          sorter={true}
          sortOrder={currentSortField === 'Audience' ? currentSortOrder : undefined}
        />
        <Column
          title="Type"
          dataIndex="Type"
          sorter={true}
          sortOrder={currentSortField === 'Type' ? currentSortOrder : undefined}
        />
        <Column
          title="Use"
          dataIndex="Use"
          sorter={true}
          sortOrder={currentSortField === 'Use' ? currentSortOrder : undefined}
        />
        <Column
          title="Request Status"
          dataIndex="Status"
          sorter={true}
          sortOrder={currentSortField === 'Status' ? currentSortOrder : undefined}
          render={this.renderRequestStatus}
        />
        <Column
          title="Created At"
          dataIndex="CreatedAt"
          sorter={true}
          sortOrder={currentSortField === 'CreatedAt' ? currentSortOrder : undefined}
        />
        <Column
          title="Expired At"
          dataIndex="ExpEpoch"
          sorter={true}
          sortOrder={currentSortField === 'ExpEpoch' ? currentSortOrder : undefined}
          render={this.renderExpiryDate}
        />
        <Column title="Action" key="action" render={this.renderActionButtons} />
      </Table>
    ) : (
      <Empty>
        <LicenseCreateModal />
      </Empty>
    );
  }
}

LicenseTable.propTypes = {
  data: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  fetchLicense: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool
};

const LicensePage = ({
  data,
  isFetching,
  isAdmin,
  fetchLicense,
  userToken,
  accessToken,
  total,
  page,
  pageSize,
  sort,
  filter
}) => {
  const [filters, setFilters] = useState([]);
  useEffect(() => {
    if (accessToken) {
      fetchLicense(1, pageSize);
    }
  }, [accessToken, pageSize, fetchLicense]);

  const handleApplyFilters = filtersArr => {
    // Filter out null values (invalid filters)
    const validFilters = filtersArr.filter(f => f !== null);

    // If no filters, show original list
    if (!validFilters.length) {
      setFilters([]);
      fetchLicense(1, pageSize, undefined, {}); // Explicitly reset filter and sort
      return;
    }
    // Convert filters array to API filter object
    const filterObj = {};
    validFilters.forEach(f => {
      if (f.field && f.operator && f.value) {
        filterObj[`${f.field}:${f.operator}`] = f.value;
      }
    });
    fetchLicense(1, pageSize, sort, filterObj);
  };

  return (
    <>
      <FilterBar filters={filters} setFilters={setFilters} onApply={handleApplyFilters} />
      <PageHeader
        title="License List"
        extra={[
          <Row type="flex" justify="space-between" align="top" key={0}>
            {data.length !== 0 && (
              <>
                <Col>
                  <Button onClick={() => fetchLicense(page, pageSize, sort, filter)}>
                    Fetch License
                  </Button>
                </Col>
                <Col className="little-margin-left">
                  <LicenseCreateModal />
                </Col>
              </>
            )}

            <Col className="little-margin-left">
              <Popover
                content={<CommandPopoverContent userToken={userToken} />}
                trigger={'click'}
                placement={'bottomRight'}
              >
                <Button>Get License with cURL</Button>
              </Popover>
            </Col>
          </Row>
        ]}
      >
        <Divider type={'horizontal'} />

        {isFetching ? (
          <div className={styles.spinContainer}>
            <Spin size={'large'} tip={'Fetching license requests...'} />
          </div>
        ) : (
          <LicenseTable
            data={data}
            isAdmin={isAdmin}
            fetchLicense={fetchLicense}
            total={total}
            page={page}
            pageSize={pageSize}
            sort={sort}
            filter={filter}
          />
        )}

        <BackTop visibilityHeight={100} />
      </PageHeader>
    </>
  );
};

const CommandPopoverContent = ({ userToken }) => {
  const cmd = `curl -H "authorization: token ${userToken}" ${process.env.REACT_APP_PROXY}/api/license/dev`;

  return (
    <div className={styles.commandPopover}>
      <div className={styles.commandPopoverHeader}>Get License with cURL</div>
      <div className={styles.commandPopoverDesc}>Run the following command in shell</div>
      <Input
        size={'small'}
        value={cmd}
        addonAfter={
          <CopyToClipboard text={cmd} onCopy={() => message.success('Command copied!')}>
            <Icon className={styles.clickable} type={'copy'} />
          </CopyToClipboard>
        }
      />
    </div>
  );
};

export default LicensePage;
